import React, { useCallback, useMemo } from "react";

import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { faFileCsv, faFileExport } from "@fortawesome/pro-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";

import { BillingDocumentAction } from "actions";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { RowSelectionMode } from "components/AgGrid/constants";
import { AgGridPreview } from "components/AgGrid/AgGridPreview";
import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import { Button } from "components/Form";
import { Column } from "components/Layout";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { AgGridTables } from "constants/aggrid";
import {
  BillingDocumentException,
  BillingDocumentStatus,
  BillingWarningsWhiteList,
} from "constants/billingDocuments";
import {
  BillingDocumentColId,
  BillingDocumentColumnDef,
  BusinessBankDetailsColId,
  BusinessBankDetailsColumnDef,
  BusinessColId,
  BusinessColumnDef,
  BusinessColumnGroup,
  FixWarningsActionColumn,
  XeroIntegrationBusinessColId,
  XeroIntegrationBusinessDef,
} from "constants/columnDefinitions";
import { DeploymentPermission } from "constants/permissions";

import {
  useHasDeploymentPermission,
  useHasXeroIntegrationPermission,
  useToggle,
} from "hooks";
import { useBillingDocumentAggridGetContextMenuItems } from "hooks/useBillingDocumentAggridGetContextMenuItems";
import { selectActiveBillingRunBillingDocumentsAggridData } from "selectors";

const getRowId = params => params.data.billingDocument.id;

const commonBillingTableColumnDefs = [
  BillingDocumentColumnDef[BillingDocumentColId.DOCUMENT_TYPE],
  BillingDocumentColumnDef[BillingDocumentColId.DOCUMENT_NUMBER],
  BusinessColumnGroup,
  BillingDocumentColumnDef[BillingDocumentColId.STATUS],
  BillingDocumentColumnDef[BillingDocumentColId.INTEGRATION_STATUS],
  BillingDocumentColumnDef[BillingDocumentColId.WARNINGS],
  BillingDocumentColumnDef[BillingDocumentColId.DUE_DATE],
  BillingDocumentColumnDef[BillingDocumentColId.TOTAL_AMOUNT_DOLLARS],
  BillingDocumentColumnDef[BillingDocumentColId.TERMS],
  BillingDocumentColumnDef[BillingDocumentColId.INTEGRATION_AMOUNT_DIFFERENCE],
  BillingDocumentColumnDef[BillingDocumentColId.INTEGRATION_DUE_DIFFERENCE],
  BillingDocumentColumnDef[BillingDocumentColId.ACTIVITY],
  BillingDocumentColumnDef[BillingDocumentColId.BILLING_REFERENCE],
];

// add relevant fields for Xero exports
const xeroBillingTableColumnDefs = [
  XeroIntegrationBusinessDef[
    XeroIntegrationBusinessColId.XERO_INTEGRATION_BUSINESS
  ],
  BillingDocumentColumnDef[BillingDocumentColId.XERO_INVOICE_ID],
];

// add relevant fields for MYOB exports
const myobBillingTableColumnDefs = [
  BusinessBankDetailsColumnDef[BusinessBankDetailsColId.HAS_BANK_DETAILS],
];

const confirmExportColumnDefs = [
  BillingDocumentColumnDef[BillingDocumentColId.DOCUMENT_TYPE],
  BillingDocumentColumnDef[BillingDocumentColId.DOCUMENT_NUMBER],
  BusinessColumnDef[BusinessColId.NAME],
  BillingDocumentColumnDef[BillingDocumentColId.STATUS],
  BillingDocumentColumnDef[BillingDocumentColId.INTEGRATION_STATUS],
  BillingDocumentColumnDef[BillingDocumentColId.WARNINGS],
  BillingDocumentColumnDef[BillingDocumentColId.DUE_DATE],
  BillingDocumentColumnDef[BillingDocumentColId.TOTAL_AMOUNT_DOLLARS],
  BillingDocumentColumnDef[BillingDocumentColId.ACTIVITY],
  BillingDocumentColumnDef[BillingDocumentColId.XERO_INVOICE_ID],
];

const ConfirmExport = ({ toggleShowConfirm, rowData }) => {
  const dispatch = useDispatch();

  const exportToXero = () => {
    dispatch(
      BillingDocumentAction.exportXeroInvoiceApi(
        rowData.map(row => row.billingDocument.id),
      ),
    );
    toggleShowConfirm();
  };

  return (
    <Dialog open onClose={toggleShowConfirm} maxWidth="lg" fullWidth>
      <DialogTitle onClose={toggleShowConfirm}>
        Are you sure you wish to export the following documents to Xero?
      </DialogTitle>
      <DialogContent>
        <ModalTableWrapper>
          <AgGridPreview
            rowData={rowData}
            columnDefs={confirmExportColumnDefs}
            tableName={AgGridTables.BILLING_DOCUMENTS_EXPORT_CONFIRMATION}
          />
        </ModalTableWrapper>
      </DialogContent>
      <DialogActions>
        <Button onClick={toggleShowConfirm}>Cancel</Button>
        <Button onClick={exportToXero}>Submit</Button>
      </DialogActions>
    </Dialog>
  );
};

export const ExportAPI = ({ billingRunId, setSelectedBillingDocuments }) => {
  const hasXeroIntegrationFeature = useHasXeroIntegrationPermission();
  const hasMyobExportsFeature = useHasDeploymentPermission(
    DeploymentPermission.featureMyobIntegration,
  );
  const hasMyobClassicIntegrationFeature = useHasDeploymentPermission(
    DeploymentPermission.featureMyobClassicIntegration,
  );
  const hasBulkBillingExportFeature = useHasDeploymentPermission(
    DeploymentPermission.featureBulkBillingExport,
  );

  const rowData = useSelector(selectActiveBillingRunBillingDocumentsAggridData);

  const getContextMenuItems = useBillingDocumentAggridGetContextMenuItems();

  const [selectedRows, setSelectedRows] = React.useState([]);

  const [showConfirm, toggleShowConfirm] = useToggle(false);

  const dispatch = useDispatch();
  const exportXeroBills = () => {
    dispatch(BillingDocumentAction.exportXeroBillCsv(billingRunId));
  };

  const exportXeroInvoices = () => {
    dispatch(BillingDocumentAction.exportXeroInvoiceCsv(billingRunId));
  };

  const exportMyob = action => {
    const billingDocumentIds = selectedRows
      .map(({ billingDocument }) => {
        return billingDocument?.id || null;
      })
      .filter(Boolean);
    dispatch(action(billingRunId, billingDocumentIds));
  };

  const exportSaleyardBulkBilling = () => {
    dispatch(BillingDocumentAction.exportSaleyardBulkBillingXml(billingRunId));
  };

  const exportAgentBulkBilling = () => {
    dispatch(BillingDocumentAction.exportAgentBulkBillingXml(billingRunId));
  };

  const isRowSelectable = ({ data }) => {
    const { billingDocument, warnings, xeroIntegrationBusiness } = data;
    const filteredWarnings = (warnings || []).filter(
      warning => !BillingWarningsWhiteList.includes(warning),
    );
    const documentIsVoidedOrDeleted = Boolean(
      warnings.find(warning =>
        [
          BillingDocumentException.VOIDED_INTEGRATION_STATUS,
          BillingDocumentException.DELETED_INTEGRATION_STATUS,
        ].includes(warning),
      ),
    );
    const canSendToXero =
      billingDocument.status === BillingDocumentStatus.APPROVED &&
      !!xeroIntegrationBusiness &&
      filteredWarnings.length === 0;

    const canSendToMyOb =
      hasMyobExportsFeature &&
      billingDocument.status !== BillingDocumentStatus.DRAFT;

    return !documentIsVoidedOrDeleted && (canSendToXero || canSendToMyOb);
  };

  const isExportDisabled = selectedRows.length < 1;

  const customExport = [
    hasXeroIntegrationFeature && {
      title: selectedRows.length
        ? `Send (${selectedRows.length}) to Xero`
        : "Send to Xero",
      isDisabled: isExportDisabled,
      onClick: toggleShowConfirm,
      icon: faPaperPlane,
      tooltip: "Select approved Invoices/RCTIs (without warnings) to send.",
    },
    hasXeroIntegrationFeature && {
      title: "Export Invoices for Xero",
      onClick: exportXeroInvoices,
      icon: faFileCsv,
      tooltip: "Download all approved Invoices as a CSV to be uploaded to Xero",
    },
    hasXeroIntegrationFeature && {
      title: "Export Account Sales/RCTIs for Xero",
      onClick: exportXeroBills,
      icon: faFileCsv,
      tooltip:
        "Download all approved Account Sales/RCTIs as a CSV to be uploaded to Xero",
    },
    hasMyobExportsFeature && {
      title: "Export Customer Cards for MYOB",
      onClick: () => exportMyob(BillingDocumentAction.exportMyobCustomerTsv),
      icon: faFileCsv,
      tooltip:
        "Download customer cards for businesses in this sale, as a CSV to be imported into MYOB",
    },
    hasMyobExportsFeature && {
      title: "Export Supplier Cards for MYOB",
      onClick: () => exportMyob(BillingDocumentAction.exportMyobSupplierTsv),
      icon: faFileCsv,
      tooltip:
        "Download supplier cards for businesses in this sale, as a CSV to be imported into MYOB",
    },
    hasBulkBillingExportFeature && {
      title: "Export Agent Bulk Billing XML",
      onClick: exportAgentBulkBilling,
      icon: faFileExport,
      tooltip: "Download all agent bulk billing Buyer Fees, as an XML file",
    },
    hasBulkBillingExportFeature && {
      title: "Export Saleyard Bulk Billing XML",
      onClick: exportSaleyardBulkBilling,
      icon: faFileExport,
      tooltip: "Download all saleyard bulk billing Buyer Fees, as an XML file",
    },
    hasMyobClassicIntegrationFeature && {
      title: "Export Sales for MYOB AccountRight Classic",
      onClick: () => exportMyob(BillingDocumentAction.exportMyobClassicSales),
      icon: faFileCsv,
      tooltip:
        "Download all approved Invoices as a CSV to be imported into MYOB as Sales - Miscellaneous",
    },
    hasMyobClassicIntegrationFeature && {
      title: "Export Purchases for MYOB AccountRight Classic",
      onClick: () =>
        exportMyob(BillingDocumentAction.exportMyobClassicPurchases),
      icon: faFileCsv,
      tooltip:
        "Download all approved Account Sales/RCTIs as a CSV to be imported into MYOB as Purchases - Miscellaneous",
    },
    hasMyobExportsFeature &&
      !hasMyobClassicIntegrationFeature && {
        title: "Export Sales for MYOB",
        onClick: () => exportMyob(BillingDocumentAction.exportMyobSales),
        icon: faFileCsv,
        tooltip:
          "Download all approved Invoices as a CSV to be imported into MYOB as Sales - Miscellaneous",
      },
    hasMyobExportsFeature &&
      !hasMyobClassicIntegrationFeature && {
        title: "Export Purchases for MYOB",
        onClick: () => exportMyob(BillingDocumentAction.exportMyobPurchases),
        icon: faFileCsv,
        tooltip:
          "Download all approved Account Sales/RCTIs as a CSV to be imported into MYOB as Purchases - Miscellaneous",
      },
  ].filter(Boolean);

  const columnDefs = useMemo(
    () =>
      commonBillingTableColumnDefs.concat(
        hasXeroIntegrationFeature ? xeroBillingTableColumnDefs : [],
        hasMyobExportsFeature ? myobBillingTableColumnDefs : [],
        FixWarningsActionColumn,
      ),
    [hasMyobExportsFeature, hasXeroIntegrationFeature],
  );

  const onRowSelectionChanged = useCallback(
    selectedRows => {
      setSelectedRows(selectedRows);
      setSelectedBillingDocuments(selectedRows.map(r => r.billingDocument));
    },
    [setSelectedBillingDocuments],
  );

  return (
    <Column full>
      <AgGridTable
        onRowSelectionChanged={onRowSelectionChanged}
        columnDefs={columnDefs}
        getRowId={getRowId}
        rowData={rowData}
        showGlobalSearchFilter={false}
        tableName={AgGridTables.BILLING_DOCUMENTS_EXPORT}
        rowSelection={RowSelectionMode.MULTIPLE}
        getContextMenuItems={getContextMenuItems}
        isRowSelectable={isRowSelectable}
        rowsSelectable
        customExport={customExport}
      />
      {showConfirm && (
        <ConfirmExport
          rowData={selectedRows}
          toggleShowConfirm={toggleShowConfirm}
        />
      )}
    </Column>
  );
};
